// A usina de cada associado — chamado #6402 (Financeiro, pedido do Igor): apurar o
// faturamento por usina sem cruzar número de UC geradora à mão.
//
// O dado já existia, mas cru: a fatura grava a UC geradora como a Equatorial
// imprime ("000406019101257") e o cadastro da UC guarda o vínculo do rateio
// (`rateio_unidade_geradora_id`). Ninguém lê "000406019101257" como Vera Cruz 02.
//
// Regra da fatura, nesta ordem:
//   1. A usina impressa na conta da distribuidora (UG 1 a 5, a primeira que for
//      da Vigor). É quem de fato mandou o crédito naquele mês — a mesma fonte da
//      aba CEO do Painel Executivo (`financeiroPorUsina`).
//   2. Sem usina impressa (avulso, conta sem injeção): a usina do cadastro da UC.
//   3. Nenhuma das duas: sem usina.
// Medido em SET/2026: 897 de 1.148 faturas pela conta, 23 pelo cadastro; das 71
// sem usina com valor, 61 são avulsos.
//
// Regra da UC: o vínculo do rateio; sem ele, o campo antigo `unidade_geradora`
// quando traz o número de uma usina (veio da planilha; também traz "Cancelado",
// "#N/D" e afins, que ficam de fora).
//
// Função pura, sem I/O nem JSX: roda sob `node --test` com type-stripping.

import { digitosUc, nomeDaUsina } from '../dashboard/painelCeo.ts';

/** Uma linha de `rateio_unidades_geradoras`, só o que a regra lê. */
export interface UsinaCadastro {
  id: string;
  nome: string;
  ativo: boolean | null;
  numero_uc_geradora: string | null;
  numero_uc_geradora_antigo: string | null;
}

export interface UsinaVigor {
  id: string;
  /** Nome curto, o mesmo da aba CEO: "Vera Cruz 02", "Centro-Sul ·9932". */
  nome: string;
}

export interface IndiceUsinas {
  /** Em ordem de nome (Centro-Sul, Morro Agudo, Vera Cruz). */
  usinas: UsinaVigor[];
  porId: Map<string, UsinaVigor>;
  /** Número da UC geradora (novo e antigo), só dígitos e sem zero à esquerda. */
  porUc: Map<string, UsinaVigor>;
}

export const INDICE_VAZIO: IndiceUsinas = { usinas: [], porId: new Map(), porUc: new Map() };

/**
 * Usina inativa fica de fora do índice — "Morrinhos (duplicada da Vera Cruz 03 -
 * não usar)" é inativa para não aparecer duas vezes.
 */
export function indiceDeUsinas(linhas: UsinaCadastro[]): IndiceUsinas {
  const usinas: UsinaVigor[] = [];
  const porId = new Map<string, UsinaVigor>();
  const porUc = new Map<string, UsinaVigor>();
  for (const l of linhas) {
    if (l.ativo === false) continue;
    const u: UsinaVigor = { id: l.id, nome: nomeDaUsina(l.nome).nome };
    usinas.push(u);
    porId.set(u.id, u);
    for (const n of [l.numero_uc_geradora, l.numero_uc_geradora_antigo].map(digitosUc)) {
      if (n) porUc.set(n, u);
    }
  }
  usinas.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { numeric: true }));
  return { usinas, porId, porUc };
}

/** O que a regra lê do cadastro da UC. */
export interface UcComUsina {
  rateio_unidade_geradora_id?: string | null;
  unidade_geradora?: string | null;
}

export function usinaDaUc(uc: UcComUsina | null | undefined, indice: IndiceUsinas): UsinaVigor | null {
  if (!uc) return null;
  if (uc.rateio_unidade_geradora_id) {
    const u = indice.porId.get(uc.rateio_unidade_geradora_id);
    if (u) return u;
  }
  const antigo = digitosUc(uc.unidade_geradora);
  return antigo ? indice.porUc.get(antigo) ?? null : null;
}

export type OrigemUsina = 'conta' | 'cadastro';

export const ROTULO_ORIGEM_USINA: Record<OrigemUsina, string> = {
  conta: 'Conta da distribuidora',
  cadastro: 'Cadastro da UC',
};

/** Os cinco campos de UC geradora que o parser lê da conta da distribuidora. */
export interface FaturaComUgs {
  unidade_geradora_1?: string | null;
  unidade_geradora_2?: string | null;
  unidade_geradora_3?: string | null;
  unidade_geradora_4?: string | null;
  unidade_geradora_5?: string | null;
}

export function usinaDaFatura(
  f: FaturaComUgs,
  uc: UcComUsina | null | undefined,
  indice: IndiceUsinas,
): { usina: UsinaVigor; origem: OrigemUsina } | null {
  for (const ug of [f.unidade_geradora_1, f.unidade_geradora_2, f.unidade_geradora_3, f.unidade_geradora_4, f.unidade_geradora_5]) {
    const n = digitosUc(ug);
    const u = n ? indice.porUc.get(n) : undefined;
    if (u) return { usina: u, origem: 'conta' };
  }
  const doCadastro = usinaDaUc(uc, indice);
  return doCadastro ? { usina: doCadastro, origem: 'cadastro' } : null;
}

export interface TotalUsina {
  /** `null` = sem usina da Vigor. */
  usinaId: string | null;
  nome: string;
  quantidade: number;
  valor: number;
}

export const NOME_SEM_USINA = 'Sem usina da Vigor';

/**
 * Soma por usina, na ordem do índice, com "sem usina" no fim. Usina sem nenhum
 * item não entra: no resumo de um mês filtrado, linha zerada só polui.
 */
export function totaisPorUsina<T>(
  itens: T[],
  usinaDe: (item: T) => UsinaVigor | null,
  valorDe: (item: T) => number,
  indice: IndiceUsinas,
): TotalUsina[] {
  const acc = new Map<string | null, { quantidade: number; valor: number }>();
  for (const item of itens) {
    const id = usinaDe(item)?.id ?? null;
    const a = acc.get(id) ?? { quantidade: 0, valor: 0 };
    a.quantidade += 1;
    a.valor += Number(valorDe(item)) || 0;
    acc.set(id, a);
  }
  const linhas: TotalUsina[] = [];
  for (const u of indice.usinas) {
    const a = acc.get(u.id);
    if (a) linhas.push({ usinaId: u.id, nome: u.nome, ...a });
  }
  const sem = acc.get(null);
  if (sem) linhas.push({ usinaId: null, nome: NOME_SEM_USINA, ...sem });
  return linhas;
}
